import moment from 'moment';
import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

interface IData {
  x: any;
  y: any;
}

interface IProps {
  color: string;
  compareData?: Array<IData>;
  data: Array<IData>;
  fontColor?: string[];
  height?: number;
  isDay?: boolean;
  isNumber?: boolean;
  legendPosition?: string;
  seriesName?: string[];
  showLabelOff?: boolean;
  strokeWidth?: number;
  width?: string;
}

const ColumnMiniChart: React.FC<IProps> = ({
  color,
  compareData,
  data,
  height,
  isNumber,
  isDay,
  legendPosition,
  showLabelOff,
  strokeWidth,
  width,
  seriesName,
}) => {
  const formattedData = useMemo(() => {
    if (!data) return {};
    const series: any = [];
    const categories: any = [];
    data.forEach(d => {
      categories.push(moment(d.x).format('MMM/DD'));
      series.push(isNumber ? d.y : (100 * d.y).toFixed(1));
    });
    return { series, categories };
  }, [data]);

  const formattedCompareData = useMemo(() => {
    if (!compareData) return {};
    const series: any = [];
    const categories: any = [];
    compareData.forEach(d => {
      categories.push(moment(d.x).format('MMM/DD'));
      series.push(isNumber ? d.y : (100 * d.y).toFixed(1));
    });
    return { series, categories };
  }, [compareData]);

  const defaultOptions = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    legend: {
      show: true,
      position: legendPosition ? legendPosition : 'bottom',
    },
    floating: true,
    axisTicks: {
      show: false,
    },
    axisBorder: {
      show: false,
    },
    labels: {
      show: false,
    },
    dataLabels: {
      enabled: showLabelOff ? false : true,
      background: {
        padding: 8,
      },
    },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150,
      },
      dynamicAnimation: {
        enabled: true,
        speed: 350,
      },
    },
    stroke: {
      curve: 'smooth',
      width: strokeWidth ? strokeWidth : 1,
      dashArray: [0, 0],
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    xaxis: {
      categories: isDay
        ? ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab']
        : formattedData.categories,
      lines: {
        show: false,
      },
    },
    yaxis: {
      lines: {
        show: false,
      },
      labels: {
        show: false,
      },
    },
    grid: {
      borderColor: 'transparent',
      strokeDashArray: 0,
    },
    colors: [color, '#3e3e3e'],
  };
  let series = [
    {
      name: seriesName ? seriesName[0] : '',
      data: formattedData.series,
    },
  ];

  series = compareData
    ? [
        ...series,
        {
          name: seriesName ? seriesName[1] : '',
          data: formattedCompareData.series,
        },
      ]
    : series;

  return (
    <Chart
      options={mergedOptions}
      series={series}
      type="bar"
      width={width ? width : '100%'}
      height={height ? height : '100'}
    />
  );
};

export default ColumnMiniChart;
